import EOrderingLogo from "@assets/images/eOrdering.png";
import EyeClose from "@assets/images/EyeClose.png";
import EyeOpen from "@assets/images/EyeOpen.png";
import SCGPLogo from "@assets/images/SCGP-color.svg";
import { Box, Button, CircularProgress, TextField } from "@material-ui/core";
import DialogUserAnnouncement from "@saleor/components/DialogUserAnnouncement";
import { FormSpacer } from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import {
  ANNOUNCE_UNTIL_DATE,
  CLOUDFLARE_TURNSTILE_IS_ENABLED,
  CLOUDFLARE_TURNSTILE_SITE_KEY
} from "@saleor/config";
import { AvailableExternalAuthenticationsQuery } from "@saleor/graphql";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { commonMessages } from "@saleor/intl";
import { IconButton } from "@saleor/macaw-ui";
import { Auth } from "aws-amplify";
import classNames from "classnames";
import { useFormik } from "formik";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import * as Yup from "yup";

import useStyles from "../styles";
import { LoginFormData } from "./form";
import { getErrorMessage } from "./messages";
export interface LoginCardProps {
  error?: any;
  disabled: boolean;
  loading: boolean;
  externalAuthentications?: AvailableExternalAuthenticationsQuery["shop"]["availableExternalAuthentications"];
  onExternalAuthentication?: (pluginId: string) => void;
  onPasswordRecovery: () => void;
  onSubmit?: (event: LoginFormData) => SubmitPromise;
}

const cookiePolicyUrl =
  "https://www.scgpackaging.com/assets/file/cookie-notice/TH_PKG_Final_Cookie%20Policy_020420.pdf";

const LoginCard: React.FC<LoginCardProps> = props => {
  const [typeLogin, setTypeLogin] = useState<number>(1); // 1 - Internal 2 - External
  const [disabledLogin, setDisabledLogin] = useState<boolean>(false);
  const [showAnnouncement, setShowAnnouncement] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const { error, disabled, loading, onPasswordRecovery, onSubmit } = props;
  const turnstileRef = useRef(null);
  const classes = useStyles(props);
  const intl = useIntl();
  const [showPassword, setShowPassword] = React.useState(false);

  const handleOpenAnnouncementPopup = () => {
    const isValidDate = moment(ANNOUNCE_UNTIL_DATE).isValid();
    if (!isValidDate) {
      return;
    }

    const outDate = moment().isAfter(ANNOUNCE_UNTIL_DATE);
    if (outDate) {
      return;
    }

    setShowAnnouncement(true);
  };

  useEffect(() => {
    const isLogout = !localStorage.getItem("amplify-type-auth");
    if (isLogout) {
      const onSignOut = async () => {
        await Auth.signOut();
        setDisabledLogin(false);
      };
      setDisabledLogin(true);
      onSignOut();
    }

    handleOpenAnnouncementPopup();
  }, []);

  useEffect(() => {
    if (
      CLOUDFLARE_TURNSTILE_IS_ENABLED &&
      typeLogin === 2 &&
      (window as any).turnstile &&
      turnstileRef.current
    ) {
      (window as any).turnstile.render(turnstileRef.current, {
        sitekey: CLOUDFLARE_TURNSTILE_SITE_KEY,
        theme: "light",
        language: "th",
        callback: token => {
          setTurnstileToken(token);
        }
      });
    }
  }, [typeLogin, error, CLOUDFLARE_TURNSTILE_IS_ENABLED]);

  const SignupSchema = Yup.object().shape({
    email: Yup.string()
      .required("Username or Email is required")
      .test(
        "email",
        "Please input external email",
        val => val && !val.includes("@scg.com")
      ),
    password: Yup.string().required("Password is required")
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: ""
    },
    onSubmit: values => {
      setTurnstileToken("");
      onSubmit({ ...values, turnstileToken });
    },
    validationSchema: SignupSchema
  });

  const onInternalClick = () => {
    setTypeLogin(1);
    Auth.federatedSignIn({
      customProvider: "scg.com"
    });

    window.localStorage.removeItem("SOLD_TO_ID");
    window.localStorage.setItem("amplify-type-auth", "INTERNAL");
  };

  const onExternalClick = () => {
    setTypeLogin(2);
    window.localStorage.removeItem("SOLD_TO_ID");
    window.localStorage.setItem("amplify-type-auth", "EXTERNAL");
  };

  const isDisabledLoginButton = useMemo(() => {
    if (CLOUDFLARE_TURNSTILE_IS_ENABLED) {
      return disabled || turnstileToken === "";
    }
    return disabled;
  }, [disabled, turnstileToken, CLOUDFLARE_TURNSTILE_IS_ENABLED]);

  if (loading) {
    return (
      <div data-testid="loadingProgress" className={classes.loading}>
        <CircularProgress size={128} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <DialogUserAnnouncement
        open={showAnnouncement}
        onClose={() => setShowAnnouncement(false)}
      />
      <div className={classes.mainPanel}>
        <div className={classes.headTextLogo}>
          <div className={classes.text}>
            <img src={SCGPLogo} alt="SCG eOrdering" />
          </div>
          <div className={classes.logo}>
            <img src={EOrderingLogo} alt={"logo"} />
          </div>
        </div>
        <div>
          {error && typeLogin === 1 && (
            <div className={classes.panel} data-testid="login-error-message">
              {getErrorMessage(error, intl)}
            </div>
          )}
          <Button
            name="sign-in-with-scg"
            id="sign-in-with-scg__btn"
            data-testid="sign-in-with-scg__btn"
            onClick={onInternalClick}
            className={classNames(
              classes.typeButton,
              "gradient",
              classes.typeButtonSCG
            )}
            disabled={disabled || disabledLogin}
            variant={typeLogin === 2 ? "outlined" : "contained"}
          >
            Sign in with &nbsp;<b>SCG Account</b>
          </Button>
          <Button
            name="sign-in-with-username"
            id="sign-in-with-username__btn"
            data-testid="sign-in-with-username__btn"
            onClick={onExternalClick}
            className={classNames(classes.typeButton, "gradient")}
            disabled={disabled}
            variant={typeLogin !== 2 ? "outlined" : "contained"}
          >
            Sign in with Username or Email
          </Button>
        </div>
        {typeLogin === 2 ? (
          <form onSubmit={formik.handleSubmit} name="login">
            <>
              {error && (
                <div
                  className={classes.panel}
                  data-testid="login-error-message"
                >
                  {error?.field === "password"
                    ? error?.message
                    : getErrorMessage("loginError", intl)}
                </div>
              )}
              <TextField
                autoFocus
                fullWidth
                autoComplete="username"
                label={intl.formatMessage(commonMessages.email)}
                name="email"
                onChange={formik.handleChange}
                value={formik.values.email}
                disabled={disabled}
                inputProps={{
                  "data-testid": "email-input"
                }}
                error={
                  (!!formik.errors.email && formik.touched.email) ||
                  error === "loginError"
                }
              />
              <div className={classes.textError}>
                {formik.errors.email && formik.touched.email
                  ? formik.errors.email
                  : ""}
              </div>
              <FormSpacer />
              <div className={classes.passwordWrapper}>
                <TextField
                  fullWidth
                  autoComplete="password"
                  label={intl.formatMessage({
                    defaultMessage: "Password"
                  })}
                  name="password"
                  onChange={formik.handleChange}
                  type={showPassword ? "text" : "password"}
                  value={formik.values.password}
                  disabled={disabled}
                  inputProps={{
                    "data-testid": "password-input"
                  }}
                  error={
                    (!!formik.errors.password && formik.touched.password) ||
                    error === "loginError"
                  }
                />
                <div className={classes.textError}>
                  {formik.errors.password && formik.touched.password
                    ? formik.errors.password
                    : ""}
                </div>
                <IconButton
                  className={classes.showPasswordBtn}
                  variant="secondary"
                  hoverOutline={false}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <img src={EyeOpen} alt={""} />
                  ) : (
                    <img src={EyeClose} alt={""} />
                  )}
                </IconButton>
              </div>
              <div ref={turnstileRef} className={classes.turnstile}></div>
              <div className={classes.buttonContainer}>
                <MyTypography
                  component="a"
                  className={classes.link}
                  onClick={onPasswordRecovery}
                  variant="body3"
                  data-testid="reset-password-link"
                >
                  <FormattedMessage
                    defaultMessage="Forgot Password ?"
                    description="description"
                  />
                </MyTypography>
                <Button
                  className={classes.loginButton}
                  disabled={isDisabledLoginButton}
                  type="submit"
                  variant={"contained"}
                  color={"secondary"}
                  name="sign_in"
                  id="sign_in"
                  data-testid="sign_in"
                >
                  <FormattedMessage
                    defaultMessage="Sign in"
                    description="button"
                  />
                </Button>
              </div>
            </>
          </form>
        ) : (
          ""
        )}
        <Box
          mt={typeLogin === 2 ? "48px" : "28px"}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <RouterLink to="/privacy-policy" className={classes.textLink}>
            {intl.formatMessage({
              defaultMessage: "นโยบายความเป็นส่วนตัว"
            })}
          </RouterLink>
          <Box className={classes.dividerLink}></Box>
          <MyTypography
            className={classes.textLink}
            variant="body3"
            onClick={() => window.open(cookiePolicyUrl, "_blank", "noopener")}
          >
            {intl.formatMessage({
              defaultMessage: "นโยบายการใช้คุ้กกี้"
            })}
          </MyTypography>
        </Box>
      </div>
    </div>
  );
};
LoginCard.displayName = "LoginCard";
export default LoginCard;
